import _ from 'lodash'
import Vue from 'vue'
import { Module, Action, getModule, Mutation, VuexModule } from 'vuex-module-decorators'
import { TCWUnexpectedError, TCWBadDataStructureError } from '@/plugins/TCWBaseError'
import { MethodType, ApiAgent } from '@/plugins/ApiRequest'
import store from '@/store'
import { IFormStore, UpdatePayload } from '@/store/types'
import { sessionStore } from '@/store/session/SessionStoreModule'
import * as ApiPath from '@/tcw/constant/ApiPath'
import { TcwFormDate } from '@/tcw/domain/types'
import { ApiGeneralSinglePayload, EditableResponse } from '@/tcw/api/types'
import * as tcw from '@/tcw/utils/TcwService'
import { KeyInfo } from '@/components/common/form/types'

@Module({
  name: 'KoutsuhiInputFormStore',
  store,
  namespaced: true,
  dynamic: true
})
export class KoutsuhiInputFormStore extends VuexModule
  implements IFormStore<ApiGeneralSinglePayload> {
  // 編集データ
  protected editTargetData: ApiGeneralSinglePayload | null = null
  // 編集前データ保持用
  protected origin: ApiGeneralSinglePayload | null = null
  // ロックフラグ
  protected lockFlg = false

  /****************
   * Getter
   ****************/
  public get ready(): boolean {
    return this.editTargetData !== null
  }
  get isNewRecord(): boolean {
    return this.editTargetData?.id === null
  }
  public get editData(): ApiGeneralSinglePayload {
    return this.editTargetData ? this.editTargetData : {}
  }
  public get isLock(): boolean {
    return this.lockFlg
  }
  public get apiPath(): { [type: string]: string } {
    return ApiPath.Paths.KoutsuhiInput
  }
  public get isChanged(): boolean {
    if (this.editTargetData === null) {
      return this.origin !== null
    }
    if (this.origin === null) {
      return this.editTargetData !== null
    }
    const destKeys = Object.keys(this.editTargetData)
    const srcKeys = Object.keys(this.origin)

    if (destKeys.length !== srcKeys.length) {
      return true
    }

    const isChanged = destKeys.some(d => {
      return this.editTargetData && this.origin ? this.editTargetData[d] !== this.origin[d] : false
    })

    return isChanged
  }

  public get targetDate(): TcwFormDate {
    if (this.editTargetData === null) {
      return null
    }
    if (typeof this.editTargetData?.hasseiBi !== 'string') {
      return null
    }
    return tcw.dateOf(this.editTargetData.hasseiBi)
  }

  public get targetDateParam(): {
    year: number
    month: number
    day: number
  } | null {
    if (this.targetDate === null) {
      return null
    }
    return {
      year: this.targetDate.getFullYear(),
      month: this.targetDate.getMonth() + 1,
      day: this.targetDate.getDate()
    }
  }

  /****************
   * Action
   ****************/

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async create(): Promise<void> {
    await this.fetchEmpty()
  }

  /**
   * 空データ取得
   */
  @Action({ rawError: true })
  public async fetchEmpty(): Promise<ApiGeneralSinglePayload> {
    // API実行
    try {
      const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
      const response: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
        this.apiPath.empty,
        'GET'
      )

      // 経路リスト初期化
      const keiroResponse: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
        ApiPath.Paths.KoutsuhiKeiro.empty,
        'GET'
      )
      const firstKeiro = Object.assign({}, keiroResponse, { bango: 1 })
      const secondKeiro = Object.assign({}, keiroResponse, { bango: 2 })
      response.keiroList = [firstKeiro, secondKeiro]

      // STOREにセットする
      this.setEditTarget(response)
      this.setLockFlg(false)

      // 呼び出し側に送る
      return Promise.resolve(response)
    } catch (e) {
      return Promise.reject(e)
    }
  }

  /**
   * 編集データセット
   * @param id 編集データID
   */
  @Action({ rawError: true })
  public async fetch(keyInfo: KeyInfo): Promise<ApiGeneralSinglePayload> {
    // API実行
    const path = ApiPath.buildPath(this.apiPath.get, keyInfo)
    const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)

    console.log('API呼び出し')
    const response: EditableResponse<ApiGeneralSinglePayload> = await apiAgent.call<
      EditableResponse<ApiGeneralSinglePayload>
    >(path, 'GET')

    if (!response.payload) {
      throw new TCWBadDataStructureError('交通費データにペイロードがありません')
    }

    console.info('交通費データを取得', response)

    // STOREにセットする
    const form = response.payload
    this.setEditTarget(form)
    this.setLockFlg(response.lock)

    // 呼び出し側に送る
    return form
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async submit(): Promise<ApiGeneralSinglePayload> {
    if (this.targetDateParam === null) {
      throw new TCWBadDataStructureError(
        '勤怠データに日付データが含まれていません。システム管理者に問い合わせてください'
      )
    }
    if (!this.ready) {
      // データがない場合はエラー
      return Promise.reject(new TCWUnexpectedError('データがセットされていません'))
    }
    // APIパラメータを生成
    const pathBase: string = this.isNewRecord ? this.apiPath.post : this.apiPath.put
    const path = ApiPath.buildPath(pathBase, this.targetDateParam)
    const method: MethodType = this.isNewRecord ? 'POST' : 'PUT'

    // API実行
    const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
    const response: ApiGeneralSinglePayload = await apiAgent.call<ApiGeneralSinglePayload>(
      path,
      method,
      this.editData
    )

    return response
  }

  /**
   * 編集データサーバ送信
   */
  @Action({ rawError: true })
  public async delete(key: KeyInfo): Promise<boolean> {
    // API実行
    const path = ApiPath.buildPath(this.apiPath.delete, key)
    const apiAgent = ApiAgent.create(tcw.getApiUrlBase(), sessionStore.sessionToken)
    await apiAgent.call(path, 'DELETE')
    return Promise.resolve(true)
  }

  @Action({ rawError: true })
  public updateRecord(payload: UpdatePayload): void {
    this.update(payload)
  }
  @Action({ rawError: true })
  public clear(): void {
    this.setEditTarget(null)
    this.setLockFlg(false)
  }

  /****************
   * Mutation
   ****************/
  @Mutation
  public update(payload: UpdatePayload): void {
    if (this.editTargetData === null) {
      throw new TCWBadDataStructureError('データがロードされていません')
    }
    Object.keys(payload).forEach((property: string) => {
      const updateValue = payload[property]
      console.debug(`update record: ${property} => ${updateValue}`)
      if (this.editTargetData) {
        // non-null-assertion回避
        Vue.set(this.editTargetData, property, updateValue)
      }
    })
  }
  @Mutation
  protected setEditTarget(record: ApiGeneralSinglePayload | null): void {
    this.editTargetData = record
    this.origin = _.cloneDeep(record)
  }
  @Mutation
  protected setLockFlg(lockFlg: boolean): void {
    this.lockFlg = lockFlg
  }
}

export const koutsuhiInputStore = getModule(KoutsuhiInputFormStore)
